import { useEffect, useState } from "react";

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const formatRemaining = (msRemaining: number): string => {
  if (msRemaining >= DAY_MS) {
    const days = Math.floor(msRemaining / DAY_MS);
    const hours = Math.floor((msRemaining % DAY_MS) / HOUR_MS);
    return `${days}j ${hours}h`;
  }
  if (msRemaining >= HOUR_MS) {
    const hours = Math.floor(msRemaining / HOUR_MS);
    const minutes = Math.floor((msRemaining % HOUR_MS) / MINUTE_MS);
    return `${hours}h ${minutes}m`;
  }
  const minutes = Math.floor(msRemaining / MINUTE_MS);
  const seconds = Math.floor((msRemaining % MINUTE_MS) / SECOND_MS);
  return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
};

const computeMsRemaining = (endsAt: string | null): number => {
  if (!endsAt) return 0;
  const endMs = new Date(endsAt).getTime();
  if (Number.isNaN(endMs)) return 0;
  return Math.max(0, endMs - Date.now());
};

type CountdownState = {
  msRemaining: number
  formatted: string | null
};

const computeState = (endsAt: string | null): CountdownState => {
  const msRemaining = computeMsRemaining(endsAt);
  return {
    msRemaining,
    formatted: msRemaining > 0 ? formatRemaining(msRemaining) : null,
  };
};

type PromotionCountdown = {
  formatted: string | null
  isExpired: boolean
};

export const usePromotionCountdown = (endsAt: string | null): PromotionCountdown => {
  const [state, setState] = useState<CountdownState>(() => computeState(endsAt));

  useEffect(() => {
    setState(computeState(endsAt));

    if (!endsAt) return;

    const timer = setInterval(() => {
      setState((prev) => {
        const next = computeState(endsAt);
        if (next.formatted === prev.formatted) return prev;
        if (next.msRemaining <= 0) {
          clearInterval(timer);
        }
        return next;
      });
    }, SECOND_MS);

    return () => clearInterval(timer);
  }, [endsAt]);

  return {
    formatted: state.formatted,
    isExpired: state.msRemaining <= 0,
  };
};
